import { type PlatformRequirementFormReference } from '@common/utils/platform-requirement-presentation';

import {
  OrganizationPlatformRequirementRuleOperator,
  PlatformRequirementDocumentVerdict,
  PlatformRequirementIssueStatus,
  PlatformRequirementProblemType,
} from '@client/graphql/__generated__/types';

/**
 * Presentational vocabulary shared by the checklist-issue surfaces.
 *
 * A leaf module by necessity, not preference: `ChecklistIssuesList` renders
 * `ChecklistIssueTooltipContent`, and the evidence view needs these same types
 * and tones. Defining them here keeps the two from importing each other. Mirrors
 * the reasoning already recorded in `platformRequirementIssueStatus.format.ts`.
 *
 * `ChecklistIssuesList` re-exports everything here, so existing importers can
 * keep pointing at the component.
 */

/**
 * Whether platform-requirement evaluation has produced data for the selected
 * checklist item. Drives the difference between an honest "no data" state (no
 * evidence evaluation ran) and a positive clean state.
 *
 * - `not_evaluated`: no evaluation evidence — the persisted view shows this
 *   whenever there are no current problems, since it cannot prove clean.
 * - `in_progress`: a live re-evaluation is running (internal live view only).
 * - `evaluated`: evaluation completed; a "No mismatch" clean state is truthful.
 * - `unavailable`: evaluation failed or errored.
 */
export type ChecklistEvaluationStatus =
  | 'not_evaluated'
  | 'in_progress'
  | 'evaluated'
  | 'unavailable';

/** A contract document identified within an attached document. */
export interface ChecklistContractDocumentView {
  form: string | null;
  /** Friendly catalog name for `form`; null falls back to the raw code. */
  formName: string | null;
  documentCategory: string | null;
}

/** A document evaluated for a checklist item, with mismatch + contract-doc context. */
export interface ChecklistEvaluationDocumentView {
  documentName: string;
  isProblemDocument: boolean;
  /**
   * The verdict this document was given. Real evidence on both surfaces: the
   * live view supplies it directly, and the persisted view carries the verdict
   * recorded against each `recordedEvaluationDocuments` entry. Absent only when
   * a caller has no verdict to give.
   */
  verdict?: PlatformRequirementDocumentVerdict | null;
  ingestionDocumentId?: string | null;
  platformDocumentId?: string | null;
  contractDocuments: ChecklistContractDocumentView[];
}

/** An evaluation document carrying the stable key its group assigned it. */
export interface ChecklistGroupedEvaluationDocumentView
  extends ChecklistEvaluationDocumentView {
  renderKey: string;
}

/**
 * A checklist document shown as context when the selected checklist item has no
 * current issue (the "None right now" empty state on the internal drawer).
 */
export interface ChecklistContextDocumentView {
  id: string;
  documentName: string;
  hasCurrentProblem: boolean;
  /** Live verdict, when known (internal live view); absent on the persisted view. */
  verdict?: PlatformRequirementDocumentVerdict | null;
  /** Identified form/category for this attachment (internal live view). */
  contractDocuments?: ChecklistContractDocumentView[];
}

/** One projected checklist mismatch issue. Surfaces map their fragment into this. */
export interface ChecklistIssueView {
  publicIssueId: string;
  status: PlatformRequirementIssueStatus;
  /**
   * What kind of problem this is. Rendered as the short reason beside the
   * checklist item name on the compact surfaces — see
   * `formatPlatformRequirementProblemType`.
   */
  problemType: PlatformRequirementProblemType;
  groupKey: string;
  /**
   * Evaluation run that produced this issue. Persisted issues carry the backing
   * problem's run id; the live preview uses the sentinel `'live'`. Rows from
   * different runs must never be merged into one group.
   */
  evaluationRunId: string;
  /** When the recorded evaluation ran. Null for the live preview. */
  evaluatedAt: unknown;
  isAgentActionable: boolean;
  checklistItemName: string;
  checklistSectionName?: string | null;
  operator: OrganizationPlatformRequirementRuleOperator;
  allowedDocumentCategories: string[];
  /**
   * Allowed forms as `{ code, name }` references so the requirement renders
   * `Friendly name (CODE)`. Legacy/live sources map codes to `{ code, name:
   * null }`. This is the single source for allowed forms; codes are derived via
   * `.code` where needed (e.g. grouping/dedup).
   */
  allowedForms: PlatformRequirementFormReference[];
  /** Documents evaluated in this run, from live or persisted evidence. */
  evaluationDocuments: ChecklistEvaluationDocumentView[];
  /**
   * Free-text reason given when this issue was ignored (the hiding review's
   * `message`). Shown with the crossed-out checklist item's evidence so the
   * reviewer sees the rationale. Undefined/null for open issues and for ignores
   * made without a reason.
   */
  ignoreReason?: string | null;
  /**
   * Who ignored this and when, from the backing `hiddenState`. Rendered as the
   * attribution line under a crossed-out group — the grey `Ignored` badge says
   * that it was handled, and only this says by whom.
   */
  ignoredBy?: string | null;
  ignoredAt?: unknown;
}

/**
 * `text-wrap: pretty` for the prose that wraps in a 320px sidebar column.
 *
 * Passed through `sx` because Chakra's style-prop system has no `textWrap`
 * mapping. It only asks the browser to avoid orphans when breaking lines, so
 * where it is unsupported the text simply wraps as it did before.
 */
export const PRETTY_WRAP = { textWrap: 'pretty' } as const;

/**
 * Tones for the agent-facing evidence panel's verdict badges.
 *
 * Deliberately separate from {@link CHECKLIST_CHIP_TONE_STYLE}: that one is the
 * internal sidebar card's redesigned vocabulary, and this panel renders on the
 * extension side panel and the agent review page, which the redesign does not
 * cover. Sharing one map would have carried the new borders, dots and warning
 * tone onto both of those surfaces.
 */
export type DocumentBadgeTone = 'error' | 'success' | 'neutral';

export const DOCUMENT_BADGE_TONE_STYLE: Record<
  DocumentBadgeTone,
  { bg: string; color: string; border?: string }
> = {
  error: {
    bg: 'compliance.notification.error.bg',
    color: 'compliance.notification.error.text',
  },
  success: {
    bg: 'compliance.notification.success.bg',
    color: 'compliance.notification.success.text',
  },
  neutral: {
    bg: 'transparent',
    color: 'compliance.text.muted',
    border: 'compliance.document.border',
  },
};

/**
 * Severity vocabulary for the internal sidebar card's chips.
 *
 * `neutral` and `outline` are both colourless, and the difference is the point:
 * `neutral` is a filled chip for a state that was *decided* but is unremarkable
 * (ignored, no data), while `outline` is a dashed chip for a state where there
 * was nothing to decide at all ("No requirement"). A dashed edge reads as
 * absence, which a filled chip cannot say.
 */
export type ChecklistChipTone =
  | 'error'
  | 'warning'
  | 'success'
  | 'neutral'
  | 'outline'
  | 'selected';

interface ChecklistChipToneStyle {
  bg: string;
  color: string;
  border?: string;
  borderStyle?: 'solid' | 'dashed';
  /**
   * Leading status dot. Only the three semantic tones carry one — it is what
   * makes severity legible before the label is read, and a dot on a colourless
   * chip would be a bullet, not a signal.
   */
  dot?: string;
}

export const CHECKLIST_CHIP_TONE_STYLE: Record<
  ChecklistChipTone,
  ChecklistChipToneStyle
> = {
  error: {
    bg: 'compliance.notification.error.bg',
    color: 'compliance.notification.error.text',
    border: 'compliance.notification.error.border',
    borderStyle: 'solid',
    dot: 'compliance.status.error',
  },
  warning: {
    bg: 'compliance.chip.unresolved.bg',
    color: 'compliance.chip.unresolved.text',
    border: 'compliance.chip.unresolved.border',
    borderStyle: 'solid',
    dot: 'compliance.status.warning',
  },
  success: {
    bg: 'compliance.notification.success.bg',
    color: 'compliance.notification.success.text',
    border: 'compliance.notification.success.border',
    borderStyle: 'solid',
    dot: 'compliance.status.success',
  },
  neutral: {
    bg: 'compliance.tag.bg',
    color: 'compliance.text.muted',
  },
  outline: {
    bg: 'transparent',
    color: 'compliance.text.muted',
    border: 'compliance.document.border',
    borderStyle: 'dashed',
  },
  // Not a verdict: marks the row the reader currently has open in the viewer.
  selected: {
    bg: 'compliance.chip.selected.bg',
    color: 'compliance.chip.selected.text',
    border: 'compliance.chip.selected.border',
    borderStyle: 'solid',
  },
};
